import React from 'react';
import ReactDOM from 'react-dom';
import * as PropTypes from "prop-types";
import scriptLoader from 'react-async-script-loader';
import payPalApi from "../../../api/payPalApi";
import {calcTotalPrice} from "./pricingHelper";
import {PRODUCT_CATEGORIES} from "../../../constants";

// These API endpoints will be called via the PayPal checkout API.
const CREATE_AGREEMENT_URL  = "/api/v1/paypal/create_agreement";
const EXECUTE_AGREEMENT_URL = "/api/v1/paypal/execute_agreement";
const CREATE_PAYMENT_URL  = "/api/v1/paypal/create_payment";
const EXECUTE_PAYMENT_URL = "/api/v1/paypal/execute_payment";

// Configure the PayPal environment.
const CLIENT = {
  sandbox: "AQ3Zt4DVFK8PkyugdvQBBoydrYqN0uN7DSaXGZhV5d3fGYMuvm9rWSsDmvo6wxy8x-Z_tlnmWZmH-_43",
  production: "AesxvrRv-rqhoHTKdJcxjOCBeviR17Ds96qFjz2NeEVUhiuLG1Tt5oXqegtg3kIQ7ocs5b1DDhopVCPB",
};

const DEFAULT_ERROR_MESSAGE = "We were unable to initiate a PayPal payment. Please make sure your entered information is correct.";

class ShapaPayPalButton extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showButton: false,
    };

    // Needed for the React PayPal button.
    window.React = React;
    window.ReactDOM = ReactDOM;
  }

  componentDidMount() {
    const {
      isScriptLoaded,
      isScriptLoadSucceed
    } = this.props;
    if (isScriptLoaded && isScriptLoadSucceed) {
      this.setState({ showButton: true });
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      isScriptLoaded,
      isScriptLoadSucceed,
    } = nextProps;
    const isLoadedButWasntLoadedBefore =
      !this.state.showButton &&
      !this.props.isScriptLoaded &&
      isScriptLoaded;
    if (isLoadedButWasntLoadedBefore) {
      if (isScriptLoadSucceed) {
        this.setState({showButton: true});
      }
    }
  }

  buildPayPalPayload() {
    const { promoCode, shippingStageState, lineItems, salesTaxRates, referralCode, adminReferralTargetCode, giftSenderInformation, portal, purchaseFlow } = this.props;
    const { country, state, shippingMethod } = shippingStageState;

    const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
    const selectedPlan = planLineItem ? planLineItem.product : null;

    // Calculate the total price of the order so the client's value can be compared to the server's value.
    const clientTotalPrice = calcTotalPrice(lineItems, country, state, shippingMethod, salesTaxRates, promoCode);

    const shippingAddress = {
      "recipient_name": `${shippingStageState.firstName} ${shippingStageState.lastName}`,
      "line1": shippingStageState.address1,
      "line2": shippingStageState.address2,
      "city": shippingStageState.city,
      "country_code": shippingStageState.country.code,
      "postal_code": shippingStageState.zipCode,
      "state": shippingStageState.state
    };

    return {
      shipping_address: shippingAddress,
      first_name: shippingStageState.firstName,
      last_name: shippingStageState.lastName,
      email: shippingStageState.email,
      phone: shippingStageState.phone,
      is_arrival_phone_call_requested: shippingStageState.isArrivalPhoneCallRequested,
      subscription_name: selectedPlan ? selectedPlan.name : null,
      referrer: localStorage.getItem("shapa_referrer"),
      promo_code_text: promoCode ? promoCode.promoCodeText : "",
      referral_code_text: referralCode,
      admin_referral_target_code: adminReferralTargetCode,
      shipping_type: shippingStageState.shippingMethod.type.toUpperCase(),

      line_items: lineItems,

      // This lets us know where the order was placed (ex. SHAPA, CLEARSTEP, etc).
      portal: portal,

      // Gives us an even more specific indicator of the current purchase flow than just the portal.
      purchase_flow: purchaseFlow,

      // So the server can compare its calculated price to the client's.
      client_total_price: clientTotalPrice,
      gift_sender_information: giftSenderInformation
    };
  }

  // Only applicable for a flow that requires account creation.
  buildUserPayload() {
    const { shippingStageState } = this.props;

    return {
      first_name: shippingStageState.firstName,
      last_name: shippingStageState.lastName,
      email: shippingStageState.email,
      phone: shippingStageState.phone,
      password: shippingStageState.password,
      password_confirmation: shippingStageState.passwordConfirmation,
      organization_name: shippingStageState.organizationName,
      organization_code: shippingStageState.organizationCode
    };
  }

  render() {
    const style = {
      size: 'responsive',
      shape: 'pill',
      color: 'gold',
      layout: 'horizontal',
      label: 'paypal',
      tagline: true
    };

    const { validate, onSuccess, onError, onCancel, isProduction, lineItems, npi, deferredUserCreate } = this.props;
    const { showButton } = this.state;

    let createUrl;
    let executeUrl;
    if (lineItems.some(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP && li.product.recurringPlan !== null)) {
      createUrl = CREATE_AGREEMENT_URL;
      executeUrl = EXECUTE_AGREEMENT_URL;
    }
    else {
      createUrl = CREATE_PAYMENT_URL;
      executeUrl = EXECUTE_PAYMENT_URL;
    }

    const payment = async () => {
      const validationResult = await validate();

      if (!validationResult) return null;

      return paypal.request.post(createUrl, { json_payload: JSON.stringify(this.buildPayPalPayload()) })
        .then(data => data.token)
        .catch(() => onError(DEFAULT_ERROR_MESSAGE));
    };

    const onAuthorize = (data) =>
      paypal.request.post(executeUrl, {
        orderID: data.orderID,
        paymentToken: data.paymentToken,
        paymentID: data.paymentID,
        payerID: data.payerID
      })
        .then(data => {
          payPalApi.createOrder(data, this.buildPayPalPayload(), this.buildUserPayload(), npi, deferredUserCreate)
            .then(response => onSuccess(response))
            .catch(response => onError(response.responseText));
        })
        .catch(error => onError(error.responseText));

    // Note from Sean: I removed the following classes because they don't seem to be working
    // when the button is embedded in ShippingStage.jsx: "position-absolute custom-paypal-container"
    return (
      <div>
        {showButton && <paypal.Button.react
          style={style}
          env={isProduction ? "production" : "sandbox"}
          client={CLIENT}
          commit={true}
          payment={payment}
          onAuthorize={onAuthorize}
          onCancel={onCancel}
          onError={() => onError(DEFAULT_ERROR_MESSAGE)}
          shippingPreference="NO_SHIPPING" // Don't allow the user to change their address within PayPal. This would potentially allow them to bypass shipping charges.
        />}
      </div>
    );
  }
}

ShapaPayPalButton.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  validate: PropTypes.func,
  isProduction: PropTypes.bool.isRequired,
  shippingStageState: PropTypes.object.isRequired,
  promoCode: PropTypes.object,
  giftSenderInformation: PropTypes.object,
  salesTaxRates: PropTypes.array.isRequired,
  referralCode: PropTypes.string,
  portal: PropTypes.string.isRequired,
  purchaseFlow: PropTypes.string.isRequired,
  lineItems: PropTypes.array.isRequired,
  adminReferralTargetCode: PropTypes.string,
  npi: PropTypes.string,
  deferredUserCreate: PropTypes.bool
};

ShapaPayPalButton.defaultProps = {
  promoCode: null,
  referralCode: null,
  validate: () => true,
  onCancel: () => { },
  npi: null
};

export default scriptLoader('https://www.paypalobjects.com/api/checkout.js')(ShapaPayPalButton);